import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Newspaper, ArrowRight, Calendar, Search, X, Clock, BookOpen, ArrowLeft } from 'lucide-react';
import { Badge } from '../components/ui';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';
import { useQuery } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { RichContentViewer } from '../components/RichContentViewer';
import { MOCK_ARTICLES } from '../api/mockData';
import preloadedArticles from '../api/preloaded-articles.json';
import { SHOULD_USE_MOCKS } from '../lib/clerk';

const formatDisplayDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime()) && (dateStr.includes('T') || dateStr.includes('-'))) {
    return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  return dateStr;
};

export function NewsroomPage() {

  const [category, setCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const { slug } = useParams();
  const navigate = useNavigate();
  const selectedArticleSlug = slug || null;
  const setSelectedArticleSlug = (newSlug: string | null) => {
    if (newSlug) {
      navigate(`/newsroom/${newSlug}`);
    } else {
      navigate('/newsroom');
    }
  };
  const [readingProgress, setReadingProgress] = useState(0);

  const categories = ['All', 'Product Update', 'Governance', 'Security', 'Best Practices'];

  const allBaselineArticles = (Array.isArray(preloadedArticles) && preloadedArticles.length > 0)
    ? preloadedArticles
    : MOCK_ARTICLES;

  const { data: articles = allBaselineArticles, isLoading } = useQuery({
    queryKey: ['public', 'articles'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/public/articles');
      return Array.isArray(res.data) ? res.data : (res.data?.content || []);
    },
    initialData: allBaselineArticles,
    staleTime: 60 * 60 * 1000 // 1 hour - baseline articles are static
  });

  // Synchronous lookup from preloaded articles for instant SSR/prerender H1 and 0-blip client hydration
  const initialArticle = selectedArticleSlug
    ? ((allBaselineArticles as any[]).find((a: any) => a.slug === selectedArticleSlug) ||
       MOCK_ARTICLES.find((a: any) => a.slug === selectedArticleSlug) ||
       (articles as any[]).find((a: any) => a.slug === selectedArticleSlug) || null)
    : null;

  const hasCompleteInitialContent = !!(initialArticle && (initialArticle as any).content);

  // Query to fetch the full article details (with content markdown) by its slug when opened
  const { data: fullArticle, isLoading: isLoadingArticle } = useQuery({
    queryKey: ['public', 'articles', selectedArticleSlug],
    queryFn: async () => {
      if (!selectedArticleSlug) return null;
      const res = await apiWorker.get(`/api/public/articles/${selectedArticleSlug}`);
      return res.data;
    },
    enabled: !!selectedArticleSlug,
    initialData: hasCompleteInitialContent ? initialArticle : undefined,
    staleTime: hasCompleteInitialContent ? 60 * 60 * 1000 : 0, // Never refetch if already preloaded
    refetchOnMount: !hasCompleteInitialContent
  });

  const activeArticle = fullArticle || initialArticle;

  const filteredArticles = articles.filter((art: any) => {
    const matchesCategory = category === 'All' || art.category === category;
    const matchesSearch = 
      art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (art.content || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const totalHeight = target.scrollHeight - target.clientHeight;
    if (totalHeight > 0) {
      setReadingProgress((target.scrollTop / totalHeight) * 100);
    }
  };

  const contactEmail = "press@orgsets.com";

  const baseSchema = activeArticle ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": activeArticle.title,
    "description": activeArticle.excerpt,
    "datePublished": activeArticle.date,
    "author": {
      "@type": "Organization",
      "name": "OrgSets"
    }
  } : {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "OrgSets Newsroom & Insights",
    "description": "The latest news, product updates, and governance articles from OrgSets."
  };

  const breadcrumbSchema = activeArticle ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://orgsets.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Newsroom",
        "item": "https://orgsets.com/newsroom"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": activeArticle.title,
        "item": `https://orgsets.com/newsroom/${selectedArticleSlug}`
      }
    ]
  } : {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://orgsets.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Newsroom",
        "item": "https://orgsets.com/newsroom"
      }
    ]
  };

  const newsroomSchema = [baseSchema, breadcrumbSchema];

  const seoTitle = activeArticle ? activeArticle.title : "Newsroom & Insights";
  const seoDesc = activeArticle ? activeArticle.excerpt : "The latest news, product updates, and governance articles from OrgSets.";

  return (
    <div className="min-h-screen transition-colors duration-300 bg-[#F5F5F0] text-black dark:bg-[#000000] dark:text-white">
      <SEO 
        title={seoTitle} 
        description={seoDesc} 
        schema={newsroomSchema}
      />
      <PublicHeader />
      
      <main className="pt-28 pb-24 px-6 max-w-7xl mx-auto">
        {!selectedArticleSlug ? (
          <>
            {/* Bauhaus Hero */}
            <header className="mb-16 grid grid-cols-1 lg:grid-cols-2 border-8 border-black dark:border-white bg-white dark:bg-[#0A0A0A]">
              <div className="p-8 md:p-12 lg:p-10 xl:p-16 flex flex-col justify-center border-b-8 lg:border-b-0 lg:border-r-8 border-black dark:border-white">
                 <div className="mb-6 self-start bg-[#0044FF] text-white px-4 py-2 font-mono uppercase tracking-widest font-bold text-sm border-2 border-black dark:border-white">
                   Newsroom & Insights
                 </div>
                 <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-black font-display tracking-tighter uppercase break-words leading-none mb-6 text-black dark:text-white">
                   Governance<br />Insights
                 </h1>
                 <p className="text-xl md:text-2xl font-medium max-w-xl text-black dark:text-white">
                   Deep dives into hierarchical systems, security audits, and strategies to eliminate volunteer administrative drag.
                 </p>
              </div>
              <div className="bg-[#FFD600] relative flex items-center justify-center min-h-[300px] lg:min-h-[500px] overflow-hidden border-black dark:border-white">
                 {/* Bauhaus geometric shapes */}
                 <div className="absolute w-64 h-64 border-8 border-black rounded-full mix-blend-multiply opacity-50 -top-10 -right-10"></div>
                 <div className="absolute w-48 h-48 bg-[#0044FF] rotate-45 mix-blend-multiply opacity-80 bottom-10 left-10 border-4 border-black"></div>
                 <div className="absolute w-32 h-32 bg-black mix-blend-overlay opacity-20 right-20 bottom-20"></div>
                 
                 <Newspaper className="w-32 h-32 text-black relative z-10" />
              </div>
            </header>

            {/* Search and Category Filter Controls */}
            <div className="mb-12 border-4 border-black dark:border-white p-4 bg-white dark:bg-[#0A0A0A] flex flex-col md:flex-row gap-4 justify-between items-center relative">
               <div className="absolute -bottom-3 -right-3 w-full h-full bg-[#0044FF] -z-10 border-4 border-black dark:border-white"></div>
               
               {/* Category Pills */}
               <div className="flex flex-wrap gap-2 w-full md:w-auto">
                 {categories.map((cat) => (
                   <button
                     key={cat}
                     onClick={() => setCategory(cat)}
                     className={cn(
                       "px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors border-2 border-black dark:border-white rounded-none",
                       category === cat 
                         ? "bg-black text-white dark:bg-white dark:text-black shadow-[4px_4px_0px_#0044FF]" 
                         : "bg-white dark:bg-[#000000] hover:bg-[#FFD600] hover:text-black dark:hover:bg-[#FFD600] dark:hover:text-black"
                     )}
                   >
                     {cat}
                   </button>
                 ))}
               </div>

               {/* Search Bar */}
               <div className="relative w-full md:w-80">
                 <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                 <input
                   type="text"
                   placeholder="SEARCH ARTICLES..."
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   className="w-full bg-white dark:bg-[#000000] text-sm pl-12 pr-4 py-3 border-2 border-black dark:border-white focus:outline-none focus:ring-0 focus:border-[#0044FF] dark:focus:border-[#FFD600] font-mono uppercase transition rounded-none text-black dark:text-white placeholder-gray-400"
                 />
                 {searchQuery && (
                   <button 
                     onClick={() => setSearchQuery('')}
                     className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:text-[#0044FF] text-black dark:text-white"
                   >
                     <X className="w-4 h-4" />
                   </button>
                 )}
               </div>
            </div>

            {/* Articles Feed - Compact List */}
            {isLoading ? (
              <div className="flex flex-col justify-center items-center py-20 space-y-4 border-8 border-black dark:border-white bg-white dark:bg-black">
                <div className="w-12 h-12 border-8 border-black dark:border-white border-t-[#0044FF] animate-spin"></div>
                <span className="text-sm font-bold uppercase tracking-widest text-black dark:text-white">Loading insights...</span>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {filteredArticles.map((article: any, idx: number) => (
                  <Link 
                     key={article.id || idx}
                     to={`/newsroom/${article.slug}`}
                     onClick={() => {
                       setSelectedArticleSlug(article.slug);
                       window.scrollTo({ top: 0, behavior: 'smooth' });
                     }}
                     className="group relative block bg-white dark:bg-[#000000] border-4 border-black dark:border-white hover:-translate-y-1 hover:shadow-[12px_12px_0px_#FFD600] dark:hover:shadow-[12px_12px_0px_#0044FF] transition-all"
                  >
                     <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
                       <div className="space-y-3 flex-1">
                         <div className="flex flex-wrap items-center gap-3 text-sm font-mono uppercase font-bold tracking-wider">
                            <span className="bg-black text-white dark:bg-white dark:text-black px-2 py-1">{article.category}</span>
                            <span className="text-black dark:text-white">{formatDisplayDate(article.date)}</span>
                            {article.readTime && (
                              <>
                                <span className="text-gray-300 dark:text-gray-700">/</span>
                                <span className="flex items-center gap-1 text-black dark:text-white"><Clock className="w-4 h-4" /> {article.readTime}</span>
                              </>
                            )}
                         </div>
                         <h2 className="text-2xl md:text-3xl lg:text-4xl font-black font-display uppercase tracking-tight text-black dark:text-white group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors line-clamp-2">
                           {article.title}
                         </h2>
                       </div>
                       
                       <div className="hidden md:flex shrink-0 w-16 h-16 bg-black text-white dark:bg-white dark:text-black items-center justify-center group-hover:bg-[#0044FF] dark:group-hover:bg-[#FFD600] group-hover:text-white dark:group-hover:text-black transition-colors border-2 border-black dark:border-white">
                         <ArrowRight className="w-8 h-8" />
                       </div>
                     </div>
                  </Link>
                ))}

                {filteredArticles.length === 0 && (
                  <div className="text-center py-20 border-8 border-black dark:border-white bg-white dark:bg-[#0A0A0A]">
                     <Newspaper className="w-16 h-16 text-black dark:text-white mx-auto mb-6" />
                     <h3 className="text-2xl font-black uppercase font-display mb-2 text-black dark:text-white">No articles found</h3>
                     <p className="font-mono text-sm uppercase text-black dark:text-white">Try selecting a different category or refining your search query.</p>
                  </div>
                )}
              </div>
            )}

            {/* Newsletter Pitch */}
            <div className="mt-24 border-8 border-black dark:border-white bg-[#0044FF] text-white p-12 text-center relative">
               <div className="absolute -bottom-4 -right-4 w-full h-full bg-black dark:bg-white -z-10 border-8 border-black dark:border-white"></div>
               <h2 className="text-4xl md:text-6xl font-black font-display uppercase tracking-tighter mb-6">Are you a journalist?</h2>
               <p className="text-xl font-medium mb-10 max-w-2xl mx-auto">
                 Contact our media relations team for press kits, branding assets, and interview requests.
               </p>
               <a href={`mailto:${contactEmail}`} className="inline-block px-10 py-5 bg-black text-white border-4 border-black hover:bg-[#FFD600] hover:text-black transition-colors font-bold uppercase tracking-widest text-lg">
                 {contactEmail}
               </a>
            </div>
          </>
        ) : (
          <div 
            className="w-full bg-white dark:bg-[#0A0A0A] border-8 border-black dark:border-white relative mb-12 mt-12"
          >
             <div className="absolute -bottom-4 -right-4 w-full h-full bg-[#0044FF] dark:bg-[#FFD600] -z-10 border-8 border-black dark:border-white"></div>

             <header className="flex items-center justify-between px-8 py-6 border-b-8 border-black dark:border-white bg-[#FFD600] dark:bg-[#0044FF]">
               <Link
                 to="/newsroom"
                 onClick={() => setSelectedArticleSlug(null)}
                 className="flex items-center gap-2 text-black dark:text-white font-bold uppercase tracking-widest hover:underline transition cursor-pointer"
               >
                 <ArrowLeft className="w-5 h-5" /> Back to Newsroom
               </Link>
             </header>

              <div className="px-8 md:px-16 py-12 md:py-20 space-y-8">
                {activeArticle ? (
                  <>
                    <div className="space-y-6">
                      <div className="flex flex-wrap items-center gap-4 text-sm font-mono uppercase font-bold tracking-wider">
                        <span className="bg-black text-white dark:bg-white dark:text-black px-3 py-1.5">{activeArticle.category}</span>
                        <span className="text-black dark:text-white">{formatDisplayDate(activeArticle.date)}</span>
                        {activeArticle.readTime && (
                          <>
                            <span className="text-gray-300 dark:text-gray-700">/</span>
                            <span className="flex items-center gap-1 text-black dark:text-white"><Clock className="w-4 h-4" /> {activeArticle.readTime}</span>
                          </>
                        )}
                      </div>
                      <h1 className="text-4xl md:text-6xl lg:text-5xl xl:text-7xl 2xl:text-[80px] font-black font-display tracking-tighter uppercase leading-none break-words text-black dark:text-white">
                        {activeArticle.title}
                      </h1>
                      <p className="text-xl md:text-2xl font-medium leading-relaxed border-l-8 border-[#0044FF] dark:border-[#FFD600] pl-6 py-2 bg-gray-50 dark:bg-white/5 text-black dark:text-white">
                        {activeArticle.excerpt}
                      </p>
                    </div>

                    <div className="pb-10 pt-8 border-t-4 border-black dark:border-white mt-12">
                      <div className="prose prose-lg dark:prose-invert prose-headings:font-display prose-headings:font-black prose-headings:uppercase prose-a:text-[#0044FF] dark:prose-a:text-[#FFD600] prose-a:font-bold max-w-none font-sans text-black dark:text-white">
                        {activeArticle.content ? (
                          <RichContentViewer content={activeArticle.content} className="" />
                        ) : isLoadingArticle ? (
                          <div className="py-12 flex flex-col items-center justify-center gap-4 border-2 border-black dark:border-white bg-gray-50 dark:bg-black/50">
                            <div className="w-8 h-8 border-4 border-black dark:border-white border-t-[#0044FF] animate-spin"></div>
                            <span className="font-mono text-xs uppercase tracking-widest text-black dark:text-white font-bold">Loading full content...</span>
                          </div>
                        ) : (
                          <p className="italic text-gray-500 font-mono text-sm">No content available for this article.</p>
                        )}
                      </div>
                      
                      {(() => {
                        const contentLower = ((activeArticle.content || '') + ' ' + (activeArticle.title || '') + ' ' + (activeArticle.slug || '')).toLowerCase();
                        const isHoa = contentLower.includes('hoa') || contentLower.includes('homeowner') || contentLower.includes('community association');
                        const isUnion = contentLower.includes('union') || contentLower.includes('chapter') || contentLower.includes('labor') || contentLower.includes('syndicate');
                        
                        if (!isHoa && !isUnion) return null;

                        return (
                          <div className="mt-20 p-8 border-8 border-black dark:border-white bg-[#FFD600] dark:bg-[#000000] relative">
                            <div className="absolute -bottom-3 -right-3 w-full h-full bg-[#0044FF] -z-10 border-4 border-black dark:border-white"></div>
                            <h3 className="text-3xl font-black font-display uppercase tracking-tight mb-6 text-black dark:text-white">Related Governance Tools</h3>
                            <ul className="space-y-4 font-bold text-lg">
                              {isHoa && (
                                <>
                                  <li>
                                    <Link to="/tools/hoa-budget-tracker" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> HOA Budget Tracker
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/tools/hoa-reserve-modeler" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> HOA Reserve Modeler
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/tools/hoa-bid-comparer" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> HOA Bid Comparer
                                    </Link>
                                  </li>
                                </>
                              )}
                              {isUnion && !isHoa && (
                                <>
                                  <li>
                                    <Link to="/tools/dues-split-calculator" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> Dues Split Calculator
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/tools/voting-weight-calculator" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> Voting Weight Calculator
                                    </Link>
                                  </li>
                                  <li>
                                    <Link to="/tools/quorum-and-roll-checker" className="text-black dark:text-white hover:text-[#0044FF] dark:hover:text-[#FFD600] uppercase tracking-wide flex items-center gap-3">
                                      <ArrowRight className="w-5 h-5" /> Quorum Checker
                                    </Link>
                                  </li>
                                </>
                              )}
                            </ul>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                ) : isLoadingArticle ? (
                  <div className="py-24 text-center">
                    <div className="w-12 h-12 border-4 border-black dark:border-white border-t-[#0044FF] animate-spin mx-auto mb-4"></div>
                    <p className="font-mono text-sm uppercase tracking-widest text-black dark:text-white">Loading article...</p>
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight mb-4 text-black dark:text-white">
                      Article Not Found
                    </h1>
                    <p className="font-mono text-sm uppercase text-gray-500 mb-6">
                      The requested newsroom article does not exist or has been moved.
                    </p>
                    <button
                      onClick={() => setSelectedArticleSlug(null)}
                      className="px-8 py-4 bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider hover:bg-[#0044FF] dark:hover:bg-[#FFD600] transition-colors"
                    >
                      Back to Newsroom
                    </button>
                  </div>
                )}
              </div>
          </div>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}


